import type { Review } from '@skop-app/lib-domain/types'
import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'

import {
  getReviewQuery,
  lightReviewsByOrganizationQuery,
  PendingReviewsCountQuery,
  reviewsByOrganizationQuery,
} from '@/services/graphql'
import { useUserContext } from '@/context'
import { getAmplifyClient } from '@/services/amplifyClient'
import { ReviewStatus } from '@skop-app/lib-types'
import { getReviewProjectId } from '@/utils/reviews'
import { reviewKeys } from './review.keys'
import { getMessagesByReview } from './message'
const client = getAmplifyClient()

type ReviewGraphQLFilters = Record<string, unknown>

// Used to get the complete review data for the popup display
export const useGetReview = (id?: string): UseQueryResult<Review> => {
  const queryClient = useQueryClient()
  const placeholderReview = useMemo(() => {
    if (!id) return undefined

    const drawerQueries = queryClient.getQueriesData<Review[]>({
      queryKey: reviewKeys.drawerPrefix,
    })
    for (const [, reviews] of drawerQueries) {
      const match = reviews?.find((review) => review.id === id)
      if (match) return match
    }
    return undefined
  }, [id, queryClient])

  const query = useQuery({
    queryKey: reviewKeys.detail(id),
    queryFn: async () => await getReview(id),
    enabled: !!id,
    placeholderData: placeholderReview,
  })

  useEffect(() => {
    if (!query.data?.id) return
    queryClient.setQueryData(
      reviewKeys.messages(query.data.id),
      query.data.messages?.items ?? [],
    )
  }, [query.data, queryClient])

  return query
}

const getReview = async (id?: string): Promise<Review> => {
  if (!id) {
    throw new Error('Review id is required')
  }

  try {
    const { data } = (await client.graphql({
      query: getReviewQuery,
      variables: { id },
    })) as GraphQLResult<{ getReview?: Review }>

    if (!data?.getReview) {
      throw new Error(`Review ${id} not found`)
    }

    return {
      ...data.getReview,
      messages: { items: await getMessagesByReview(id) },
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

// Used to get the complete reviews data for the panel display
export const useReviews = (
  filters?: ReviewGraphQLFilters,
): UseQueryResult<Review[]> => {
  const { organizationScope } = useUserContext()
  const organizationID = organizationScope.activeOrganizationId

  return useQuery({
    queryKey: reviewKeys.drawer(organizationID, filters),
    queryFn: async () =>
      await getReviewsByOrganization(organizationID, filters),
    staleTime: 1000 * 60 * 2,
    enabled: !!organizationID,
    placeholderData: [],
  })
}

const getReviewsByOrganization = async (
  organizationID: string | undefined,
  filters?: ReviewGraphQLFilters,
) => {
  try {
    const items: Review[] = []
    let nextToken: string | null = null

    do {
      const {
        data: { reviewsByOrganization },
      } = (await client.graphql({
        query: reviewsByOrganizationQuery,
        variables: {
          organizationID,
          limit: 1000,
          nextToken,
          ...(filters ? { filter: filters } : {}),
        },
      })) as GraphQLResult<{
        reviewsByOrganization: { items: Review[]; nextToken?: string | null }
      }>

      items.push(...(reviewsByOrganization.items ?? []))
      nextToken = reviewsByOrganization.nextToken ?? null
    } while (nextToken)

    const reviews: Review[] = []
    for (const review of items) {
      reviews.push({
        ...review,
        messages: { items: await getMessagesByReview(review.id) },
      })
    }
    return reviews
  } catch (e) {
    console.error(e)
    throw e
  }
}

// Used to get the light reviews data for the project resources grid display
export const useGridReviews = (
  projectID?: string,
): UseQueryResult<Review[]> => {
  const { organizationScope } = useUserContext()
  const organizationID = organizationScope.activeOrganizationId

  return useQuery({
    queryKey: reviewKeys.grid(organizationID),
    queryFn: async () => await getGridReviewsByOrganization(organizationID),
    staleTime: 1000 * 60 * 2,
    enabled: !!organizationID,
    placeholderData: [],
    select: (data) => {
      if (!projectID) return data
      return data.filter((review) => getReviewProjectId(review) === projectID)
    },
  })
}

export const useLightReviews = (projectID?: string): UseQueryResult<Review[]> =>
  useGridReviews(projectID)

const getGridReviewsByOrganization = async (
  organizationID: string | undefined,
) => {
  try {
    const items: Review[] = []
    let nextToken: string | null = null

    do {
      const {
        data: { reviewsByOrganization },
      } = (await client.graphql({
        query: lightReviewsByOrganizationQuery,
        variables: {
          organizationID,
          limit: 1000,
          nextToken,
          filter: {
            status: {
              eq: ReviewStatus.PENDING,
            },
          },
        },
      })) as GraphQLResult<{
        reviewsByOrganization: { items: Review[]; nextToken?: string | null }
      }>

      items.push(...(reviewsByOrganization.items ?? []))
      nextToken = reviewsByOrganization.nextToken ?? null
    } while (nextToken)

    return items
  } catch (e) {
    console.error(e)
    throw e
  }
}

// Used to get the pending reviews count for the header notifications display
export const usePendingReviewsCount = (): UseQueryResult<number> => {
  const { organizationScope } = useUserContext()
  const organizationID = organizationScope.activeOrganizationId

  return useQuery<number>({
    queryKey: reviewKeys.count(organizationID),
    queryFn: async () => await getPendingReviewsCount(organizationID),
    staleTime: 1000 * 60 * 2,
    enabled: !!organizationID,
    placeholderData: 0,
  })
}

const getPendingReviewsCount = async (organizationID: string | undefined) => {
  try {
    let count = 0
    let nextToken: string | null = null

    do {
      const {
        data: { reviewsByOrganization },
      } = (await client.graphql({
        query: PendingReviewsCountQuery,
        variables: {
          organizationID,
          limit: 1000,
          nextToken,
          filter: {
            status: {
              eq: ReviewStatus.PENDING,
            },
          },
        },
      })) as GraphQLResult<{
        reviewsByOrganization: { items: Review[]; nextToken?: string | null }
      }>

      count += reviewsByOrganization?.items?.length ?? 0
      nextToken = reviewsByOrganization?.nextToken ?? null
    } while (nextToken)

    return count
  } catch (e) {
    console.error(e)
    throw e
  }
}
